import { pixelsToMm } from '@/utils/unitsUtils';
import { v4 as uuidv4 } from 'uuid';
import fontManagerService from '@/utils/fontManagerService';
import { useColor } from '@/composables/color';
import { TEXT_MASK_EFFECTS } from '@/utils/textMaskEffects';
import { ValueFormat } from '@/typings/designStudio/sourceElementTypes';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('TextService', textService);

    function textService(MultipleFontService, ElementsColorService, ProductService) {
        const { getColorObj } = useColor();
        const objectsWithTextMasks = {};
        const objectsWithTextCutouts = {};

        function _updateTextElements(text, brotherTexts, canvas) {
            // Update each brother text but don't do it in a loop because it will result in very poor
            //  performance on products with many text elements
            var update = function (start, count, objects, doRender) {
                if (doRender) {
                    for (var n = start; n < start + count && n < objects.length; ++n) {
                        objects[n].set('text', text);
                    }
                    canvas.requestRenderAll();
                    start += count;
                }

                if (start < objects.length) {
                    // Skip every 1 animation frame to decrease CPU load
                    doRender = !doRender;
                    requestAnimationFrame(() => update(start, count, objects, doRender));
                }
            };

            requestAnimationFrame(() => update(0, 5, brotherTexts, false));
        }

        const debouncedUpdateTextElements = _.debounce(_updateTextElements, 500);

        function updateTexts(text, object, canvas) {
            // Update the modified text element
            object.set('text', text);
            canvas.renderAll();

            // Find objects with the same id
            const brotherTexts = canvas.getObjects().filter(function (o) {
                if (o.get('id') === object.id && o !== object) {
                    return o;
                }
            });

            // If we have too many texts, debounce the function, we don't want to update the text elements so quickly
            if (brotherTexts.length > 40) {
                debouncedUpdateTextElements(text, brotherTexts, canvas);
            } else {
                _updateTextElements(text, brotherTexts, canvas);
            }
        }

        function buildBoundedTextProperties(parameters, override = {}) {
            const defaultFont = fontManagerService.defaultFont;
            const { id, canvasWidth, canvasHeight, itext, zIndex, pageEpsId, strokeDashArray, name } = parameters;
            const fontsMap = new Map();
            fontsMap.set(1, defaultFont.Path);
            const shadowColorObj = getColorObj({ hex: '#7258E0', a: 1 });
            const defaultShadowColor = MultipleFontService.createColorObj({
                hex: shadowColorObj.hex,
                cmyk: `${shadowColorObj.cmyk.c},${shadowColorObj.cmyk.m},${shadowColorObj.cmyk.y},${shadowColorObj.cmyk.k}`, 
            });
            const defaultColor = MultipleFontService.createColorObj({
                hex: '#000000',
                cmyk: '0,0,0,1'
            });
            const fontColorsMap = new Map([[1, defaultColor]]);
            const outlineColorsMap = new Map([[1, defaultColor]]);
            const strokeColorsMap = new Map([[1, defaultColor]]);
            const shadowColorsMap = new Map([[1, defaultShadowColor]]);
            const left = canvasWidth / 2;
            const top = canvasHeight / 2;
            const width = 0.3 * canvasWidth;
            const height = 0.3 * canvasHeight;

            return {
                outlineWidth: 0,
                textAlign: 'center',
                fontColorOption: 1,
                outlineColorOption: 1,
                tracking: 0,
                lineSpacing: 1,
                skewX: 0,
                skewY: 0,
                angle: 0,
                rotation: 0,
                locked: false,
                flipX: false,
                flipY: false,
                text: '',
                width,
                height,
                originX: 'center',
                originY: 'center',
                left,
                top,
                strokeDashArray,
                centeredRotation: true,
                lockScalingFlip: true,

                minSizePx: fabric.util.parseUnit(15 + 'pt'),
                maxSizePx: fabric.util.parseUnit(50 + 'pt'),
                multiline: false,
                verticalText: false,
                caps: false,
                fontPath: defaultFont.Path,
                fontName: defaultFont.Name + '_' + defaultFont.Style,
                fontFamily: defaultFont.Name + '_' + defaultFont.Style,
                fontOption: 1,

                color: fontColorsMap.get(1),
                outlineColor: outlineColorsMap.get(1),
                outlineColorsMap,
                id,
                uuid: uuidv4(),
                zIndex,
                fontsMap,
                fontColorsMap,
                strokeColor: strokeColorsMap.get(1),
                strokeWidth: 0,
                strokeColorsMap,
                strokeColorOption: 1,
                Itext: itext,
                defaultValue: '',
                isPersonalizableText: true,
                prefix: '',
                suffix: '',
                betweenCharacters: '',
                betweenWords: '',
                ligatures: true,
                referenceId: null,
                isOverlay: false,
                isPrintArea: false,
                pageEpsId,
                visible: true,
                name: name || '',
                allowEdit: false,
                useAsMask: false,
                useAsCutout: false,
                shapeConfig: null,
                autofillSource: '',
                dateFormat: {},
                shadowType: null,
                shadowOffset: 0,
                shadowAngle: 0,
                shadowBlur: 0,
                shadowColor: shadowColorsMap.get(1),
                shadowColorsMap,
                shadowColorOption: 1,
                sourceElement: null,
                ...override
            };
        }

        function generateBoundedText(parameters, override = {}) {
            const defaultText = buildBoundedTextProperties(parameters, override);

            const { flipX, flipY } = parameters;
            const text = new (parameters.isTextPath ? fabric.TextTransform : fabric.BoundedText)(defaultText);

            text.setFlip(flipX || false, flipY || false);
            return text;
        }

        function buildCircularTextProperties(parameters, override = {}) {
            const defaultFont = fontManagerService.defaultFont;
            let fontsMap = new Map();
            // set default option to the fonts map
            fontsMap.set(1, defaultFont.Path);
            const defaultColor = MultipleFontService.createColorObj({
                hex: '#000000',
                cmyk: '0,0,0,1'
            });
            const fontColorsMap = new Map([[1, defaultColor]]);
            const outlineColorsMap = new Map([[1, defaultColor]]);
            const { canvasWidth, canvasHeight, id, zIndex, pathWidth, pageEpsId } = parameters;
            return {
                pathWidth,
                width: 0.2 * canvasWidth,
                height: 0.2 * canvasWidth,
                originX: 'center',
                originY: 'center',
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                centeredRotation: true,
                lockSkewingX: true,
                lockSkewingY: true,
                lockScalingFlip: true,
                verticalDiameter: 0.3 * canvasWidth,
                horizontalDiameter: 0.3 * canvasWidth,

                fill: '#000000',
                color: fontColorsMap.get(1),

                stroke: '#000000',
                outlineColor: '#000000',
                strokeWidth: 0,
                outlineColorsMap,
                outlineColorOption: 1,

                fontPath: defaultFont.Path,
                fontName: defaultFont.Name + '_' + defaultFont.Style,
                fontFamily: defaultFont.Name + '_' + defaultFont.Style,
                fontSize: fabric.util.parseUnit(10 + 'pt'),
                maxSizePx: fabric.util.parseUnit(10 + 'pt'),
                minSizePx: fabric.util.parseUnit(0 + 'pt'),
                fontOption: 1,
                id,
                uuid: uuidv4(),
                // add fonts map to circular text object
                fontsMap: fontsMap,
                // add font color map to circular text object
                fontColorsMap: fontColorsMap,
                zIndex,
                defaultValue: '',
                isPersonalizableText: true,
                prefix: '',
                suffix: '',
                betweenCharacters: '',
                betweenWords: '',
                referenceId: null,
                isOverlay: false,
                isPrintArea: false,
                pageEpsId,
                ...override
            };
        }

        function generatePathIText(parameters, override = {}) {
            const { flipX, flipY } = parameters;
            const circularTextParams = buildCircularTextProperties(parameters, override);

            const text = new fabric.PathIText('A', circularTextParams);
            text.setFlip(flipX || false, flipY || false);
            return text;
        }

        function loadTextData($scope) {
            if (!$scope.textSelected) {
                return;
            }
            // Load data
            $scope.selectedTextProperties.text = $scope.textSelected.getText();
            $scope.selectedTextProperties.centerX = pixelsToMm($scope.textSelected.left, $scope.DPI);
            $scope.selectedTextProperties.centerY = pixelsToMm($scope.textSelected.top, $scope.DPI);
            $scope.selectedTextProperties.rotation = $scope.textSelected.angle;

            const w = $scope.textSelected.getWidth();
            const h = $scope.textSelected.getHeight();
            $scope.selectedTextProperties.width = pixelsToMm(w, $scope.DPI);
            $scope.selectedTextProperties.height = pixelsToMm(h, $scope.DPI);

            $scope.selectedTextProperties.minSizePx = fabric.util.pxToPt($scope.textSelected.minSizePx);
            $scope.selectedTextProperties.maxSizePx = fabric.util.pxToPt($scope.textSelected.maxSizePx);
            $scope.selectedTextProperties.multiline = $scope.textSelected.multiline;
            $scope.selectedTextProperties.verticalText = $scope.textSelected.verticalText;
            $scope.selectedTextProperties.caps = $scope.textSelected.caps;
            $scope.selectedTextProperties.fontPath = $scope.textSelected.fontPath;

            if ($scope.hasOwnProperty('searchFont')) {
                const selectedFont = fontManagerService.findFontByPath($scope.selectedTextProperties.fontPath);
                if (selectedFont) {
                    const fontName = `${selectedFont.Name} (${selectedFont.Style})`;
                    $scope.searchFont = {
                        selectedText: fontName,
                        previousSelectedText: fontName,
                        previousSelectedValue: $scope.selectedTextProperties.fontPath,
                        searchText: ''
                    };
                } else {
                    $scope.searchFont = {
                        selectedText: '',
                        previousSelectedText: '',
                        previousSelectedValue: '',
                        searchText: ''
                    };
                }
            }

            $scope.selectedTextProperties.id = $scope.textSelected.id;
            $scope.selectedTextProperties.skewX = $scope.textSelected.skewX;
            $scope.selectedTextProperties.skewY = $scope.textSelected.skewY;
            $scope.selectedTextProperties.currentFontSize = fabric.util.pxToPt($scope.textSelected.getFontSize());
            $scope.currentFontSizeValidator();
            $scope.selectedTextProperties.textAlign = $scope.textSelected.getTextAlign();
            $scope.selectedTextProperties.textVerticalAlign = $scope.textSelected.getTextVerticalAlign();
            $scope.selectedTextProperties.outlineWidth = $scope.textSelected.getOutlineWidth();
            $scope.selectedTextProperties.fontOption = $scope.textSelected.fontOption || 1;
            $scope.selectedTextProperties.fontColorOption = $scope.textSelected.fontColorOption || 1;
            $scope.selectedTextProperties.outlineColorOption = $scope.textSelected.outlineColorOption || 1;
            $scope.selectedTextProperties.Itext = $scope.textSelected.Itext;
            $scope.selectedTextProperties.editedSize = $scope.textSelected.editedSize;
            $scope.selectedTextProperties.tracking = $scope.textSelected.getTrackingAmount();
            $scope.selectedTextProperties.lineSpacing = $scope.textSelected.lineSpacing * 100;
            $scope.selectedTextProperties.locked = $scope.textSelected.locked;
            $scope.selectedTextProperties.defaultValue = $scope.textSelected.defaultValue;
            $scope.selectedTextProperties.isPersonalizableText = $scope.textSelected.isPersonalizableText !== false;
            $scope.selectedTextProperties.prefix = $scope.textSelected.prefix;
            $scope.selectedTextProperties.suffix = $scope.textSelected.suffix;
            $scope.selectedTextProperties.betweenCharacters = $scope.textSelected.betweenCharacters;
            $scope.selectedTextProperties.betweenWords = $scope.textSelected.betweenWords;
            $scope.selectedTextProperties.referenceId = $scope.textSelected.referenceId;
            $scope.selectedTextProperties.texture = $scope.textSelected.texture;
            $scope.selectedTextProperties.textureScale = $scope.textSelected.textureScale || 100;
            const flip = $scope.textSelected.getFlip();
            $scope.selectedTextProperties.flipX = flip.flipX || false;
            $scope.selectedTextProperties.flipY = flip.flipY || false;
            $scope.selectedTextProperties.allowEdit = $scope.textSelected.allowEdit;
            $scope.selectedTextProperties.oldAllowEdit = $scope.textSelected.oldAllowEdit;
            $scope.selectedTextProperties.isTextPath = $scope.textSelected.isTextPath || false;
            $scope.selectedTextProperties.useAsMask = $scope.textSelected.useAsMask || false;
            $scope.selectedTextProperties.useAsCutout = $scope.textSelected.useAsCutout || false;
            $scope.selectedTextProperties.autofillSource = $scope.textSelected.autofillSource || '';
            $scope.selectedTextProperties.dateFormat = $scope.textSelected.dateFormat || {};
            $scope.selectedTextProperties.shadowType = $scope.textSelected.shadowType;
            $scope.selectedTextProperties.shadowOffset = $scope.textSelected.shadowOffset || 0;
            $scope.selectedTextProperties.shadowAngle = $scope.textSelected.shadowAngle || 0;
            $scope.selectedTextProperties.shadowBlur = $scope.textSelected.shadowBlur || 0;
            $scope.selectedTextProperties.sourceElement = $scope.textSelected.sourceElement;
        }

        function loadCircularTextData($scope) {
            if (!$scope.textSelected) {
                return;
            }
            // Load data
            $scope.selectedTextProperties.text = $scope.textSelected.text;
            $scope.selectedTextProperties.centerX = pixelsToMm($scope.textSelected.left, $scope.DPI);
            $scope.selectedTextProperties.centerY = pixelsToMm($scope.textSelected.top, $scope.DPI);
            $scope.selectedTextProperties.rotation = $scope.textSelected.angle;
            $scope.selectedTextProperties.verticalDiameter = pixelsToMm(
                $scope.textSelected.verticalDiameter,
                $scope.DPI
            );
            $scope.selectedTextProperties.horizontalDiameter = pixelsToMm(
                $scope.textSelected.horizontalDiameter,
                $scope.DPI
            );

            $scope.selectedTextProperties.startAngle = $scope.textSelected.startAngle;
            $scope.selectedTextProperties.endAngle = $scope.textSelected.endAngle;

            $scope.selectedTextProperties.caps = $scope.textSelected.caps;
            $scope.selectedTextProperties.fontPath = $scope.textSelected.fontPath;

            if ($scope.hasOwnProperty('searchFont')) {
                const selectedFont = fontManagerService.findFontByPath($scope.selectedTextProperties.fontPath);
                if (selectedFont) {
                    const fontName = `${selectedFont.Name} (${selectedFont.Style})`;
                    $scope.searchFont = {
                        selectedText: fontName,
                        previousSelectedText: fontName,
                        previousSelectedValue: $scope.selectedTextProperties.fontPath,
                        searchText: ''
                    };
                } else {
                    $scope.searchFont = {
                        selectedText: '',
                        previousSelectedText: '',
                        previousSelectedValue: '',
                        searchText: ''
                    };
                }
            }

            $scope.selectedTextProperties.id = $scope.textSelected.id;
            const fontSize = fabric.util.pxToPt($scope.textSelected.fontSize);
            $scope.selectedTextProperties.currentFontSize = fontSize;
            $scope.selectedTextProperties.fontSize = fontSize;
            $scope.selectedTextProperties.minSizePx =
                $scope.textSelected.minSizePx != undefined
                    ? fabric.util.pxToPt($scope.textSelected.minSizePx)
                    : fontSize;
            $scope.selectedTextProperties.maxSizePx =
                $scope.textSelected.maxSizePx != undefined
                    ? fabric.util.pxToPt($scope.textSelected.maxSizePx)
                    : fontSize;
            $scope.currentFontSizeValidator();
            $scope.selectedTextProperties.textAlign = $scope.textSelected.textAlign;
            $scope.selectedTextProperties.stroke = $scope.textSelected.stroke;
            $scope.selectedTextProperties.outlineColor = $scope.textSelected.stroke;
            $scope.selectedTextProperties.outlineWidth = $scope.textSelected.strokeWidth;
            $scope.selectedTextProperties.textSide = $scope.textSelected.convex ? 'convex' : 'concave';
            $scope.selectedTextProperties.tracking = $scope.textSelected.tracking;
            $scope.selectedTextProperties.fontOption = $scope.textSelected.fontOption || 1;
            $scope.selectedTextProperties.fontColorOption = $scope.textSelected.fontColorOption || 1;
            $scope.selectedTextProperties.outlineColorOption = $scope.textSelected.outlineColorOption || 1;
            $scope.selectedTextProperties.locked = $scope.textSelected.locked;
            $scope.selectedTextProperties.defaultValue = $scope.textSelected.defaultValue;
            $scope.selectedTextProperties.isPersonalizableText = $scope.textSelected.isPersonalizableText !== false;
            $scope.selectedTextProperties.prefix = $scope.textSelected.prefix;
            $scope.selectedTextProperties.suffix = $scope.textSelected.suffix;
            $scope.selectedTextProperties.betweenCharacters = $scope.textSelected.betweenCharacters;
            $scope.selectedTextProperties.betweenWords = $scope.textSelected.betweenWords;
            $scope.selectedTextProperties.referenceId = $scope.textSelected.referenceId;
            const flip = $scope.textSelected.getFlip();
            $scope.selectedTextProperties.flipX = flip.flipX || false;
            $scope.selectedTextProperties.flipY = flip.flipY || false;
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} color color object.
         * @param {boolean} save validates the persistent of the color change.
         */
        function textOutlineColor($scope, canvas, color, save = false) {
            $scope.textSelected.setOutlineColor(ElementsColorService.colorToWeb(color));
            if (save) {
                const colorObj = MultipleFontService.createColorObj({
                    alpha: color.alpha,
                    hex: '#' + color.HEX,
                    cmyk: color.cmyk.c + ',' + color.cmyk.m + ',' + color.cmyk.y + ',' + color.cmyk.k
                });
                $scope.textSelected.outlineColor = colorObj;
            }
            canvas.renderAll();
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} colors colors objects.
         * @param {boolean} save validates the persistent of the color change.
         * @param {boolean} showInfoDialog show information dialog
         */
        function textFillColor($scope, canvas, colors, save = false, showInfoDialog = true) {
            $scope.textSelected.setColor(ElementsColorService.colorToWeb(colors));
            canvas.requestRenderAll();

            if (save) {
                MultipleFontService.saveFontColorToOption(
                    colors,
                    canvas.getActiveObject(),
                    $scope.editMode,
                    showInfoDialog
                );
            }
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} colors colors objects.
         * @param {boolean} save validates the persistent of the color change.
         */
        function textCircularColorFill($scope, canvas, colors, save = false) {
            $scope.textSelected.set('fill', ElementsColorService.colorToWeb(colors));
            canvas.renderAll();
            if (save) {
                MultipleFontService.saveFontColorToOption(colors, canvas.getActiveObject(), $scope.editMode);
            }
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} color color object.
         * @param {boolean} _save validates the persistent of the color change.
         */
        function textCircularOutlineColor($scope, canvas, color, _save = false) {
            $scope.textSelected.set('opentypeStroke', ElementsColorService.colorToWeb(color));
            $scope.textSelected.set('stroke', ElementsColorService.colorToWeb(color));

            canvas.renderAll();
        }

        const MASK_EFFECT = { ...TEXT_MASK_EFFECTS.mask, storage: objectsWithTextMasks };
        const CUTOUT_EFFECT = { ...TEXT_MASK_EFFECTS.cutout, storage: objectsWithTextCutouts };

        function _addObjectWithEffect(effect, textUuid, targetObject) {
            effect.storage[textUuid] = targetObject.uuid;
        }

        function _removeObjectWithEffect(effect, textUuid) {
            delete effect.storage[textUuid];
        }

        function _applyTextEffect(canvas, $scope, uuid = '', text = '', effect) {
            return new Promise((resolve) => {
                let textObject = null;
                if (!uuid) {
                    textObject = canvas.getActiveObject();
                    uuid = textObject ? textObject.uuid : '';
                }

                if (!uuid) { resolve({ applied: false }); return; }

                textObject = textObject || canvas._objects.find((o) => o.uuid === uuid && o[effect.prop]);
                if (!textObject || !textObject[effect.prop]) { resolve({ applied: false }); return; }

                let targetObject;
                if (Object.keys(effect.storage).length) {
                    targetObject = canvas._objects.find(
                        (o) => o.uuid === effect.storage[uuid] && o.type === 'image-placeholder'
                    );
                }

                if (!targetObject) {
                    const link = (ProductService.getElementsLinks() || []).find(
                        (l) => l.sourceId === uuid && l.valueFormat === effect.valueFormat
                    );
                    if (link) {
                        targetObject = canvas._objects.find(
                            (o) => o.uuid === link.targetId && o.type === 'image-placeholder'
                        );
                        if (targetObject) _addObjectWithEffect(effect, uuid, targetObject);
                    }
                }

                if (!targetObject) {
                    const claimedTargetIds = new Set(
                        (ProductService.getElementsLinks() || [])
                            .filter((l) => l.valueFormat === ValueFormat.TextAsMask || l.valueFormat === ValueFormat.TextAsCutout)
                            .map((l) => l.targetId)
                    );
                    const candidates = canvas._objects
                        .filter(
                            (o) =>
                                o.type === 'image-placeholder' &&
                                o.zIndex < textObject.zIndex &&
                                !claimedTargetIds.has(o.uuid)
                        )
                        .sort((a, b) => b.zIndex - a.zIndex);

                    if (!candidates.length) { resolve({ applied: false }); return; }
                    targetObject = candidates[0];
                    _addObjectWithEffect(effect, textObject.uuid, targetObject);
                }

                textObject.set('text', textObject.text || text);

                if (textObject.text) {
                    fabric.Image.fromURL(textObject.getTextAsMask(), function (clipPath) {
                        clipPath.top = textObject.top;
                        clipPath.left = textObject.left;
                        clipPath.originX = textObject.originX;
                        clipPath.originY = textObject.originY;
                        clipPath.scaleX = textObject.scaleX;
                        clipPath.scaleY = textObject.scaleY;
                        clipPath.absolutePositioned = true;
                        clipPath.angle = textObject.angle;
                        if (effect.inverted) clipPath.inverted = true;
                        targetObject.item(0).set('clipPath', clipPath);
                        if ($scope.editMode) targetObject.setEditedProp('hasTextMask');
                        targetObject.set('hasTextMask', true);
                        canvas.fire('object:textMask:changed', { target: targetObject });
                        canvas.renderAll();
                        resolve({ applied: true, targetObject });
                    });
                } else {
                    resolve({ applied: false, targetObject });
                }
            });
        }

        function _removeTextEffect(canvas, $scope, targetObject, textObject, effect) {
            if (!textObject && targetObject) {
                const link = (ProductService.getElementsLinks() || []).find(
                    (l) => l.targetId === targetObject.uuid && l.valueFormat === effect.valueFormat
                );
                textObject = link ? canvas._objects.find((o) => o.uuid === link.sourceId) : null;
            }
            if (textObject) {
                if ($scope.textAsMaskStoredObjects[textObject.uuid]) {
                    setTextMaskColor(canvas, $scope, textObject, $scope.textAsMaskStoredObjects[textObject.uuid]);
                    setTextMaskStroke(canvas, $scope, textObject, $scope.textAsMaskStoredObjects[textObject.uuid]);
                }
                delete $scope.textAsMaskStoredObjects[textObject.uuid];

                if (!targetObject) {
                    const link = (ProductService.getElementsLinks() || []).find(
                        (l) => l.sourceId === textObject.uuid && l.valueFormat === effect.valueFormat
                    );
                    targetObject = link ? canvas._objects.find((o) => o.uuid === link.targetId && o.type === 'image-placeholder') : null;
                }
            }

            if (targetObject?.hasTextMask) {
                targetObject.set('hasTextMask', false);
                if ($scope.editMode) targetObject.setEditedProp('hasTextMask');
                if (textObject) _removeObjectWithEffect(effect, textObject.uuid);
                if (targetObject.item(0).clipPath) targetObject.item(0).set('clipPath', null);
                canvas.fire('object:textMask:changed', { target: targetObject });
                canvas.renderAll();
            }

            return targetObject;
        }

        // Public API — thin wrappers over the unified implementation
        function addObjectWithTextMask(textUuid, targetObject) { _addObjectWithEffect(MASK_EFFECT, textUuid, targetObject); }
        function removeObjectWithTextMask(textUuid) { _removeObjectWithEffect(MASK_EFFECT, textUuid); }
        function addObjectWithTextCutout(textUuid, targetObject) { _addObjectWithEffect(CUTOUT_EFFECT, textUuid, targetObject); }
        function removeObjectWithTextCutout(textUuid) { _removeObjectWithEffect(CUTOUT_EFFECT, textUuid); }
        function applyTextMask(canvas, $scope, uuid, text) { return _applyTextEffect(canvas, $scope, uuid, text, MASK_EFFECT); }
        function applyTextCutout(canvas, $scope, uuid, text) { return _applyTextEffect(canvas, $scope, uuid, text, CUTOUT_EFFECT); }
        function removeTextMask(canvas, $scope, targetObject, textObject) { return _removeTextEffect(canvas, $scope, targetObject, textObject, MASK_EFFECT); }
        function removeTextCutout(canvas, $scope, targetObject, textObject) { return _removeTextEffect(canvas, $scope, targetObject, textObject, CUTOUT_EFFECT); }

        async function processTextMask(canvas, $scope, objects) {
            for (const object of objects) {
                const maskKey = Object.keys(objectsWithTextMasks).find((k) => objectsWithTextMasks[k] === object.uuid);
                if (maskKey) {
                    await applyTextMask(canvas, $scope, maskKey);
                }

                const cutoutKey = Object.keys(objectsWithTextCutouts).find(
                    (k) => objectsWithTextCutouts[k] === object.uuid
                );
                if (cutoutKey) {
                    await applyTextCutout(canvas, $scope, cutoutKey);
                }
            }
        }

        function setTextMaskColor(canvas, $scope, object, copy) {
            if ($scope.editMode) {
                object.setEditedProp('fontColorsMap', 'color');
            }

            let fontColor;
            if (object.useAsMask || object.useAsCutout) {
                fontColor = {
                    alpha: 0,
                    cmyk: {
                        c: 0,
                        m: 0,
                        y: 0,
                        k: 1
                    },
                    hex: '#000000',
                    HEX: '#000000'
                };
            } else {
                fontColor =
                    JSON.stringify(copy.fontColor).indexOf('NaN') > -1
                        ? 'rgb(0,0,0)'
                        : copy.fontColor === 'rgba(0,0,0,0)'
                        ? 'rgb(0,0,0)'
                        : copy.fontColor;
                fontColor = MultipleFontService.upgradeOldColor(fontColor);
            }

            object.fontColorsMap.set(1, fontColor);
            object.fontColorOption = 1;
            object.setColor(ElementsColorService.colorToWeb(fontColor));
            canvas.requestRenderAll();
        }

        function setTextMaskStroke(canvas, $scope, object, copy) {
            if ($scope.editMode) {
                object.setEditedProp('outlineColorsMap', 'outlineColor', 'outlineWidth');
            }

            let strokeColor, strokeWidth;
            if (object.useAsMask || object.useAsCutout) {
                strokeColor = {
                    alpha: 1,
                    cmyk: {
                        c: 0.8095238095238095,
                        m: 0.6952380952380952,
                        y: 0,
                        k: 0.17647058823529416
                    },
                    hex: '#2840D2',
                    HEX: '#2840D2'
                };
                strokeWidth = 1;
            } else {
                strokeColor = copy.strokeColor.indexOf('NaN') > -1 ? 'rgb(0,0,0)' : copy.strokeColor;
                strokeColor = MultipleFontService.upgradeOldColor(strokeColor);
                strokeWidth = copy.strokeWidth || 0;
            }

            object.outlineColorsMap.set(1, strokeColor);
            object.outlineColorOption = 1;
            object.setOutlineColor(ElementsColorService.colorToWeb(strokeColor));
            object.setOutlineWidth(strokeWidth);
            canvas.requestRenderAll();
        }

        function _toMapEntries(map) {
            if (!map) return [];
            if (map instanceof Map) return Array.from(map.entries());
            if (Array.isArray(map)) return map;
            if (typeof map === 'string') {
                try {
                    return JSON.parse(map);
                } catch {
                    return [];
                }
            }
            return [];
        }

        function getPatternReplicaObjects(canvas, originUuid) {
            const links = ProductService.getPatternReplicaLinks(originUuid);
            return links
                .map((link) => canvas._objects.find((o) => o.uuid === link.targetId))
                .filter(Boolean);
        }

        function buildPatternReplica(canvas, origin, cell, options = {}) {
            const replica = origin.clone();
            replica.uuid = options.uuid || uuidv4();
            replica.id = origin.id;
            replica.isPatternReplica = true;
            replica.patternOriginUuid = origin.uuid;
            replica.pattern = false;
            replica.patternConfig = null;
            replica.patternCell = { row: cell.row, col: cell.col };
            replica.set('locked', true);
            if (typeof options.zIndex === 'number') {
                replica.set('zIndex', options.zIndex);
            }
            replica.set('angle', origin.angle);
            if (options.left != null && options.top != null) {
                replica.set({ left: options.left, top: options.top });
            }
            replica.addedOnEdit = !!options.editMode;
            if (replica.setCoords) replica.setCoords();
            canvas.add(replica);
            return replica;
        }

        function repositionPatternReplica(origin, replica, position, resize) {
            replica.set('angle', origin.angle);
            if (resize) replica.setScale(origin.getWidth(), origin.getHeight(), true);
            const originText = origin.getText();
            if (replica.getText() !== originText) replica.setText(originText);
            if (position) replica.set({ left: position.left, top: position.top });
            if (replica.setCoords) replica.setCoords();
        }

        async function syncPatternReplica(canvas, origin, replica, position) {
            replica.set('caps', origin.caps);
            replica.set('prefix', origin.prefix);
            replica.set('suffix', origin.suffix);
            replica.set('betweenCharacters', origin.betweenCharacters);
            replica.set('betweenWords', origin.betweenWords);
            replica.set('lineSpacing', origin.lineSpacing);
            replica.set('ligatures', origin.ligatures);
            replica.set('textAlign', origin.textAlign);
            replica.set('textVerticalAlign', origin.textVerticalAlign);
            
            replica.setMultiline(origin.multiline);
            replica.setVerticalText(origin.verticalText);
            replica.setTrackingAmount(origin.getTrackingAmount());
            replica.setMinSizePx(origin.minSizePx);
            replica.setMaxSizePx(origin.maxSizePx);
            replica.setOutlineWidth(origin.getOutlineWidth());

            // Font
            replica.fontPath = origin.fontPath;
            replica.fontName = origin.fontName;
            replica.fontOption = origin.fontOption || 1;
            replica.fontsMap = new Map(_toMapEntries(origin.fontsMap));

            // Colors
            replica.fontColorOption = origin.fontColorOption || 1;
            replica.fontColorsMap = new Map(_toMapEntries(origin.fontColorsMap));
            replica.outlineColorOption = origin.outlineColorOption || 1;
            replica.outlineColorsMap = new Map(_toMapEntries(origin.outlineColorsMap));

            // Library references
            replica.set('fontLibraryId', origin.fontLibraryId);
            replica.set('colorLibraryId', origin.colorLibraryId);
            replica.set('outlineColorLibraryId', origin.outlineColorLibraryId);
            replica.set('strokeColorLibraryId', origin.strokeColorLibraryId);
            replica.set('shadowColorLibraryId', origin.shadowColorLibraryId);

            // Shadow
            replica.set('shadowType', origin.shadowType);
            replica.set('shadowOffset', origin.shadowOffset);
            replica.set('shadowAngle', origin.shadowAngle);
            replica.set('shadowBlur', origin.shadowBlur);
            replica.shadowColorOption = origin.shadowColorOption || 1;
            replica.shadowColorsMap = new Map(_toMapEntries(origin.shadowColorsMap));

            replica.setText(origin.getText());

            // Apply font/colors/shadow using the same services the load path relies on.
            await MultipleFontService.changeFontOption(replica, replica.fontOption, canvas);
            await MultipleFontService.changeFontColorOption(undefined, replica, replica.fontColorOption, canvas);
            await MultipleFontService.changeOutlineColorOption(replica, replica.outlineColorOption, canvas);
            MultipleFontService.changeShadowColorOption(replica, replica.shadowColorOption, canvas);

            replica.setScale(origin.getWidth(), origin.getHeight(), true);
            replica.set('angle', origin.angle);
            if (position) replica.set({ left: position.left, top: position.top });
            if (replica.setCoords) replica.setCoords();
        }

        return {
            updateTexts: updateTexts,
            loadTextData: loadTextData,
            loadCircularTextData: loadCircularTextData,
            textCircularColorFill: textCircularColorFill,
            textFillColor: textFillColor,
            textOutlineColor: textOutlineColor,
            textCircularOutlineColor: textCircularOutlineColor,
            generateBoundedText: generateBoundedText,
            generatePathIText: generatePathIText,
            buildBoundedTextProperties: buildBoundedTextProperties,
            buildCircularTextProperties: buildCircularTextProperties,
            addObjectWithTextMask: addObjectWithTextMask,
            removeObjectWithTextMask: removeObjectWithTextMask,
            applyTextMask: applyTextMask,
            removeTextMask: removeTextMask,
            addObjectWithTextCutout: addObjectWithTextCutout,
            removeObjectWithTextCutout: removeObjectWithTextCutout,
            applyTextCutout: applyTextCutout,
            removeTextCutout: removeTextCutout,
            processTextMask: processTextMask,
            setTextMaskColor: setTextMaskColor,
            setTextMaskStroke: setTextMaskStroke,
            getPatternReplicaObjects: getPatternReplicaObjects,
            buildPatternReplica: buildPatternReplica,
            repositionPatternReplica: repositionPatternReplica,
            syncPatternReplica: syncPatternReplica
        };
    }
})();
